import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Dashboard,
  DashboardIsActive,
  History,
  HistoryIsActive,
  Logout,
  LogoutIsActive,
  Notification,
  NotificationIsActive,
  Payment,
  PaymentIsActive,
  Profile,
  ProfileIsActive,
  RaiseComplaint,
  RaiseComplaintIsActive,
  Setting,
  SettingIsActive,
} from "Assets/SvgImage";
import ColorData from "Components/CstmTheme/ColorData";
import ImgPath from "Constants/Imgs";
import { RouteUrls } from "Constants/RouteUrls";
import { SIDEBAR_CONFIG } from "Constants/SidebarConfig";
import SidebarToggleButton from "./SidebarToggleButton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";

export const navItems = [
  {
    label: "Home",
    inactiveIcon: (
      <HomeIcon
        sx={{
          color: ColorData.text.secondary,
          ":hover": { color: ColorData.hover.main },
        }}
      />
    ),
    activeIcon: (
      <HomeIcon
        sx={{
          color: ColorData.hover.main,
          ":hover": { color: ColorData.hover.main },
        }}
      />
    ),
    path: "/home.html",
  },
  {
    label: "Dashboard",
    inactiveIcon: <Dashboard />,
    activeIcon: <DashboardIsActive />,
    path: RouteUrls.dashboard,
  },
  {
    label: "Order History",
    inactiveIcon: <History />,
    activeIcon: <HistoryIsActive />,
    path: RouteUrls.OrderHistory,
  },
  {
    label: "Create Case",
    inactiveIcon: <ImgPath.CaseCreationIcon sx={{ color: "custom.white" }} />,
    activeIcon: <ImgPath.CaseCreationIcon sx={{ color: "primary.main" }} />,
    path: RouteUrls.case,
  },
  {
    label: "Profile",
    inactiveIcon: <Profile />,
    activeIcon: <ProfileIsActive />,
    path: RouteUrls.userProfileForm,
  },
  {
    label: "Setting",
    inactiveIcon: <Setting />,
    activeIcon: <SettingIsActive />,
    path: "",
  },
  {
    label: "Payment",
    inactiveIcon: <Payment />,
    activeIcon: <PaymentIsActive />,
    path: "",
  },
  {
    label: "Notification",
    inactiveIcon: <Notification />,
    activeIcon: <NotificationIsActive />,
    path: "",
  },
  {
    label: "Raise Complaint",
    inactiveIcon: <RaiseComplaint />,
    activeIcon: <RaiseComplaintIsActive />,
    path: "",
  },
  {
    label: "Logout",
    inactiveIcon: <Logout />,
    activeIcon: <LogoutIsActive />,
    path: "/",
  },
];

const Sidebar = ({
  drawerWidth,
  isCollapsed,
  onToggleCollapse,
  onClose,
  handleLogout,
}: {
  drawerWidth: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onClose?: () => void;
  handleLogout?: () => void;
}) => {
  const [activeItem, setActiveItem] = useState("Dashboard");
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();
  const handleItemClick = (label: string, path: string) => {
    if (label === "Logout") {
      handleLogout?.();
    } else {
      setActiveItem(label);
      onClose?.();

      // If it's an external path or .html page, do full redirect
      if (path?.endsWith(".html") || path?.startsWith("http")) {
        window.location.href = path;
      } else {
        navigate(path);
      }
    }
  };

  return (
    <Box
      sx={{
        width: drawerWidth,
        height: "100vh",
        bgcolor: "primary.main",
        px: isCollapsed ? 1 : 2,
        py: 3,
        position: "relative",
        transition: `all ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
        display: "flex",
        flexDirection: "column",
        // overflow: "hidden",
      }}
    >
      {/* Toggle Button */}
      <SidebarToggleButton
        isCollapsed={isCollapsed}
        onToggle={onToggleCollapse}
      />

      {/* Logo Section */}
      <Box
        display="flex"
        justifyContent={isCollapsed ? "center" : "start"}
        sx={{
          transition: `all ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {!isCollapsed ? (
          <img
            src={"https://nolegalpaisa.com/assets/images/nlp_logo.png"}
            alt="Logo"
            style={{
              objectFit: "contain",
              width: 200,
              height: 80,
              transition: `all ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
              cursor: "pointer",
              filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))",
            }}
            onClick={() => {
              window.location.href = "/home.html";
            }}
          />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: "white",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "primary.main",
              cursor: "pointer",
            }}
            onClick={() => {
              window.location.href = "/home.html";
            }}
          >
            NLP
          </Box>
        )}
      </Box>

      {/* Navigation List */}
      <List sx={{ 
        overflowY: "auto", 
        flex: 1,
        mt: 3,
        minHeight: 0, // Important for flex child to be scrollable
      }}>
        {navItems.map(({ label, inactiveIcon, activeIcon, path }) => {
          const isActive = label === activeItem;
          const isHovered = label === hoveredItem;

          const NavigationItem = (
            <ListItemButton
              key={label}
              onClick={() => handleItemClick(label, path)}
              onMouseEnter={() => setHoveredItem(label)}
              onMouseLeave={() => setHoveredItem(null)}
              sx={{
                borderRadius: 2,
                mb: 1.5,
                px: isCollapsed ? 1 : 2,
                py: 1.2,
                minHeight: 48,
                justifyContent: isCollapsed ? "center" : "flex-start",
                bgcolor:
                  isActive || isHovered ? "#f0e8e4" : ColorData.primary.main,
                "&:hover": {
                  bgcolor: "#f0e8e4",
                  color: ColorData.primary.main,
                },
                transition: `all ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? "auto" : 32,
                  justifyContent: "center",
                  transition: `all ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
                }}
              >
                {isActive || isHovered ? activeIcon : inactiveIcon}
              </ListItemIcon>

              {!isCollapsed && (
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 400,
                        color:
                          isActive || isHovered
                            ? ColorData.primary.main
                            : ColorData.text.secondary,
                        "&:hover": {
                          color: ColorData.primary.main,
                        },
                        opacity: isCollapsed ? 0 : 1,
                        transition: `opacity ${SIDEBAR_CONFIG.animationDuration}ms ease-in-out`,
                      }}
                    >
                      {label}
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          );

          // Wrap with Tooltip when collapsed
          return isCollapsed ? (
            <Tooltip key={label} title={label} placement="right" arrow>
              {NavigationItem}
            </Tooltip>
          ) : (
            NavigationItem
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
